import React, { useState, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
    BugAntIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
    ClockIcon,
    ExclamationCircleIcon,
    UserIcon,
    CommandLineIcon,
    ArchiveBoxIcon
} from '@heroicons/react/24/outline';

const displayStatus = (s) => {
    if (!s) return '';
    if (s === 'Retesting In Progress') return 'QA In Progress';
    if (s === 'In Progress') return 'Dev In Progress';
    return s;
};

const UnifiedTimeline = ({ bugs = [], mode = 'timeline', hideTitle = false }) => {
    const [visibleCount, setVisibleCount] = useState(10);

    // Process bugs into a unified list of events
    const allEvents = useMemo(() => {
        const events = [];

        if (!bugs || bugs.length === 0) return [];

        bugs.forEach(bug => {
            if (bug.statusHistory && bug.statusHistory.length > 0) {
                bug.statusHistory.forEach((history, index) => {
                    let type = 'status_update';
                    let actionLabel = 'Updated status';
                    let icon = ArrowPathIcon;
                    const itemLabel = (bug.ticketType || bug.category || 'Bug').toLowerCase();


                    if (index === 0) {
                        type = 'bug_logged';
                        actionLabel = `created this ${itemLabel}`;
                        icon = BugAntIcon;

                    } else {
                        const oldStatus = displayStatus(bug.statusHistory[index - 1].status);
                        const newStatus = displayStatus(history.status);
                        const statusLower = (history.status || '').toLowerCase();

                        if (statusLower === 'in progress') {
                            actionLabel = `started working on this ${itemLabel}`;
                            icon = ClockIcon;

                        } else {
                            actionLabel = `changed status from ${oldStatus} to ${newStatus}`;

                            if (statusLower === 'fixed') {
                                icon = CheckCircleIcon;

                            } else if (statusLower === 'rejected' || statusLower === 'not a bug') {
                                icon = XCircleIcon;

                            } else if (statusLower === 'reopen' || statusLower === 'reopened') {
                                icon = ExclamationCircleIcon;

                            } else if (statusLower === 'closed') {
                                icon = CheckCircleIcon;

                            } else if (statusLower === 'deferred') {
                                icon = ClockIcon;
                                // visual hint variables removed (unused)
                            } else if (statusLower === 'deprecated') {
                                icon = ArchiveBoxIcon;
                                // visual hint variables removed (unused)
                            }
                        }
                    }

                    const rawRole = (history.changedBy?.role || 'developer').toLowerCase();

                    events.push({
                        id: `${bug._id}-${index}`,
                        bugMongoId: bug._id,
                        bugCode: bug.bugId,
                        bugTitle: bug.title,
                        type,
                        actionLabel,
                        status: history.status,
                        prevStatus: index > 0 ? bug.statusHistory[index - 1]?.status : undefined,
                        user: history.changedBy?.name || 'Unknown User',
                        userAvatar: history.changedBy?.avatar,
                        role: rawRole,
                        timestamp: new Date(history.changedAt),
                        description: history.description || '',
                        icon
                    });
                });
            }

            if (bug.comments && bug.comments.length > 0) {
                bug.comments.forEach((comment, index) => {
                    const rawRole = (comment.user?.role || 'developer').toLowerCase();

                    events.push({
                        id: `${bug._id}-comment-${index}`,
                        bugMongoId: bug._id,
                        bugCode: bug.bugId,
                        bugTitle: bug.title,
                        type: 'comment_added',
                        actionLabel: 'added a comment',
                        user: comment.user?.name || 'Unknown User',
                        userAvatar: comment.user?.avatar,
                        role: rawRole,
                        timestamp: new Date(comment.createdAt),
                        description: comment.text,
                        icon: ChatBubbleLeftRightIcon
                    });

                    if (comment.replies && comment.replies.length > 0) {
                        comment.replies.forEach((reply, rIndex) => {
                            const replyRole = (reply.user?.role || 'developer').toLowerCase();
                            events.push({
                                id: `${bug._id}-comment-${index}-reply-${rIndex}`,
                                bugMongoId: bug._id,
                                bugCode: bug.bugId,
                                bugTitle: bug.title,
                                type: 'reply_added',
                                actionLabel: 'replied to a comment',
                                user: reply.user?.name || 'Unknown User',
                                userAvatar: reply.user?.avatar,
                                role: replyRole,
                                timestamp: new Date(reply.createdAt),
                                description: reply.text,
                                icon: ChatBubbleLeftRightIcon
                            });
                        });
                    }
                });
            }

            if (bug.history && bug.history.length > 0) {
                bug.history.forEach((histItem, index) => {
                    if (histItem.type === 'status' || histItem.type === 'comment') return;
                    if (histItem.type === 'creation' && bug.statusHistory?.length > 0) return;

                    let type = histItem.type;
                    const itemLabel = (bug.ticketType || bug.category || 'Bug').toLowerCase();
                    let actionLabel = histItem.action || `updated the ${itemLabel}`;
                    let icon = CommandLineIcon;

                    if (type === 'creation') {
                        actionLabel = `created this ${itemLabel}`;
                        icon = BugAntIcon;
                    } else if (type === 'assignment') {
                        // Guard against raw MongoDB ObjectIds stored in old history records
                        const isRawId = /^[a-f0-9]{24}$/i.test((histItem.description || '').replace(/^assigned to\s*/i, '').trim());
                        if (histItem.description && !isRawId) {
                            actionLabel = histItem.description.toLowerCase();
                        } else {
                            actionLabel = `assigned this ${itemLabel}`;
                        }
                        icon = UserIcon;
                    } else if (type === 'update') {
                        if (histItem.description) {
                            if (histItem.description.startsWith('Updated fields:')) {
                                const fields = histItem.description.replace('Updated fields:', '').trim();
                                const formattedFields = fields.split(',')
                                    .map(f => f.trim())
                                    .map(f => f.charAt(0).toUpperCase() + f.slice(1))
                                    .join(', ');
                                actionLabel = `updated ${formattedFields}`;
                            } else {
                                // Keep casing for potential user names or values
                                actionLabel = histItem.action || histItem.description;
                            }
                        } else {
                            actionLabel = `updated ${itemLabel} details`;
                        }
                        icon = ArrowPathIcon;
                    }

                    const rawRole = (histItem.changedBy?.role || 'tester').toLowerCase();

                    events.push({
                        id: `${bug._id}-hist-${index}`,
                        bugMongoId: bug._id,
                        bugCode: bug.bugId,
                        bugTitle: bug.title,
                        type,
                        actionLabel,
                        user: histItem.changedBy?.name || 'System',
                        userAvatar: histItem.changedBy?.avatar,
                        role: rawRole,
                        timestamp: new Date(histItem.changedAt),
                        // For assignment events, suppress raw ObjectId descriptions
                        description: type === 'assignment'
                            ? (/^[a-f0-9]{24}$/i.test((histItem.description || '').replace(/^assigned to\s*/i, '').trim()) ? '' : (histItem.description || ''))
                            : (histItem.action && histItem.description && histItem.description !== histItem.action ? histItem.description : (histItem.action ? '' : (histItem.description || ''))),
                        icon
                    });
                });
            }
        });

        return events.sort((a, b) => b.timestamp - a.timestamp);
    }, [bugs]);

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    if (allEvents.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-[#FAFBFC] rounded-lg border border-[#DFE1E6]">
                <ClockIcon className="h-8 w-8 text-[#6B778C] mb-3 opacity-50" />
                <h3 className="text-sm font-bold text-[#172B4D]">No activity yet</h3>
                <p className="text-xs text-[#6B778C] mt-1">Updates will appear here as they happen.</p>
            </div>
        );
    }

    const displayedEvents = allEvents.slice(0, visibleCount);

    if (mode === 'tree') {
        return (
            <div className="relative py-6">
                {/* Center vertical line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-px bg-[#DFE1E6]" />

                {/* Headers - highlighted and always visible */}
                <div className="flex justify-between w-full max-w-5xl mx-auto px-4 mb-4 text-[11px] uppercase tracking-widest">
                    <div className="w-1/2 text-center pr-12 font-bold text-[#0052CC]">Developer Activity</div>
                    <div className="w-1/2 text-center pl-12 font-bold text-[#36B37E]">Tester Activity</div>
                </div>

                <div className="space-y-2">
                    {displayedEvents.map((event) => (
                        <div key={event.id} className="relative flex items-center justify-between w-full group">
                            {/* Left side: developer */}
                            <div className={`w-[45%] flex ${event.role === 'developer' ? 'justify-end' : 'justify-end invisible'}`}>
                                {event.role === 'developer' && (
                                    <div className="relative flex flex-col items-end pr-8 text-right">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <div className="w-6 h-6 rounded-full bg-[#0052CC] text-white text-[10px] font-bold flex items-center justify-center overflow-hidden">
                                                {event.userAvatar ? <img src={event.userAvatar} alt={event.user} className="w-full h-full object-cover" /> : event.user.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-xs font-bold text-[#172B4D]">{event.user}</span>
                                            <span className="text-[10px] text-[#6B778C]">{formatDistanceToNow(event.timestamp, { addSuffix: true })}</span>
                                        </div>
                                        <div className="text-xs text-[#42526E] leading-tight">
                                            {event.type === 'status_update' && event.prevStatus
                                                ? <>changed status from <span className="font-semibold text-[#172B4D]">{displayStatus(event.prevStatus)}</span> to <span className="font-semibold text-[#172B4D]">{displayStatus(event.status)}</span></>
                                                : <span className="font-medium text-[#172B4D]">{event.actionLabel}</span>}
                                        </div>

                                        {/* Hover tooltip (left) - clickable */}
                                        <Link to={`/bugs/${event.bugMongoId || event.bugCode}`} className="opacity-0 invisible group-hover:opacity-100 group-hover:visible absolute top-0 right-full mr-4 w-64 bg-white rounded-md shadow-lg border border-[#DFE1E6] p-3 z-50 block cursor-pointer">
                                            <div className="text-[10px] text-[#6B778C] mb-1">{event.actionLabel.includes('observation') ? 'Observation' : 'Bug'}</div>
                                            <div className="text-xs font-bold text-[#0052CC] truncate">{event.bugTitle || String(event.bugCode || event.bugMongoId)}</div>
                                            <div className="mt-1 text-[11px] text-[#172B4D]">
                                                {event.type === 'status_update' && event.prevStatus
                                                    ? <>changed status from <span className="font-semibold">{displayStatus(event.prevStatus)}</span> to <span className="font-semibold">{displayStatus(event.status)}</span></>
                                                    : event.actionLabel}
                                            </div>
                                        </Link>
                                        <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible absolute top-2 right-0 -mr-1 w-2 h-2 bg-white border-t border-r border-[#DFE1E6] transform rotate-45 z-40"></div>
                                    </div>
                                )}
                            </div>

                            {/* Center node */}
                            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">
                                <Link to={`/bugs/${event.bugMongoId || event.bugCode}`} className="h-8 w-8 rounded-full border-4 border-white shadow-sm flex items-center justify-center bg-[#F4F5F7] cursor-pointer">
                                    <event.icon className={`h-4 w-4 ${event.role === 'developer' ? 'text-[#0052CC]' : 'text-[#36B37E]'}`} />
                                </Link>
                            </div>

                            {/* Right side: tester */}
                            <div className={`w-[45%] flex ${event.role !== 'developer' ? 'justify-start' : 'justify-start invisible'}`}>
                                {event.role !== 'developer' && (
                                    <div className="relative flex flex-col items-start pl-8 text-left">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <div className="w-6 h-6 rounded-full bg-[#36B37E] text-white text-[10px] font-bold flex items-center justify-center overflow-hidden">
                                                {event.userAvatar ? <img src={event.userAvatar} alt={event.user} className="w-full h-full object-cover" /> : event.user.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-xs font-bold text-[#172B4D]">{event.user}</span>
                                            <span className="text-[10px] text-[#6B778C]">{formatDistanceToNow(event.timestamp, { addSuffix: true })}</span>
                                        </div>
                                        <div className="text-xs text-[#42526E] leading-tight">
                                            {event.type === 'status_update' && event.prevStatus
                                                ? <>changed status from <span className="font-semibold text-[#172B4D]">{displayStatus(event.prevStatus)}</span> to <span className="font-semibold text-[#172B4D]">{displayStatus(event.status)}</span></>
                                                : <span className="font-medium text-[#172B4D]">{event.actionLabel}</span>}
                                        </div>

                                        {/* Hover tooltip (right) - clickable */}
                                        <Link to={`/bugs/${event.bugMongoId || event.bugCode}`} className="opacity-0 invisible group-hover:opacity-100 group-hover:visible absolute top-0 left-full ml-4 w-64 bg-white rounded-md shadow-lg border border-[#DFE1E6] p-3 z-50 block cursor-pointer">
                                            <div className="text-[10px] text-[#6B778C] mb-1">{event.actionLabel.includes('observation') ? 'Observation' : 'Bug'}</div>
                                            <div className="text-xs font-bold text-[#0052CC] truncate">{event.bugTitle || String(event.bugCode || event.bugMongoId)}</div>
                                            <div className="mt-1 text-[11px] text-[#172B4D]">
                                                {event.type === 'status_update' && event.prevStatus
                                                    ? <>changed status from <span className="font-semibold">{displayStatus(event.prevStatus)}</span> to <span className="font-semibold">{displayStatus(event.status)}</span></>
                                                    : event.actionLabel}
                                            </div>
                                        </Link>
                                        <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible absolute top-2 left-0 -ml-1 w-2 h-2 bg-white border-b border-l border-[#DFE1E6] transform rotate-45 z-40"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {visibleCount < allEvents.length && (
                    <div className="mt-6 flex justify-center">
                        <button
                            onClick={handleLoadMore}
                            className="text-[11px] font-bold text-[#0052CC] hover:text-[#0747A6] flex items-center gap-1 py-1 px-3 rounded hover:bg-[#DEEBFF] transition-all"
                        >
                            <ArrowPathIcon className="h-3 w-3" />
                            Load More
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Default list mode (Bug Details page)
    return (
        <div className="relative pt-2 pb-6">
            {!hideTitle && <h3 className="text-[11px] font-bold text-[#6B778C] uppercase tracking-widest mb-6">Activity Timeline</h3>}

            <div className="relative space-y-0 pl-1">
                {/* Vertical Connector Line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-[1px] bg-[#DFE1E6]" />

                {displayedEvents.map((event) => (
                    <div key={event.id} className="relative pl-8 pb-6 last:pb-2 group">
                        {/* Dot Indicator */}
                        <div className="absolute left-0 top-1.5 w-8 h-8 flex items-center justify-center z-10">
                            <div className={`w-2 h-2 rounded-full border-2 border-white ring-1 ring-[#DFE1E6] ${event.type === 'bug_logged' ? 'bg-blue-500' :
                                event.type === 'comment_added' || event.type === 'reply_added' ? 'bg-sky-500' :
                                    event.status === 'Fixed' ? 'bg-green-500' :
                                        event.status === 'Rejected' ? 'bg-red-500' :
                                            'bg-[#6B778C]'
                                }`} />
                        </div>

                        <div className="flex flex-col">
                            <div className="flex items-center gap-2 mb-1">
                                {/* Avatar */}
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0 ${event.role === 'developer' ? 'bg-[#0052CC]' : 'bg-[#36B37E]'
                                    } overflow-hidden`}>
                                    {event.userAvatar ? (
                                        <img src={event.userAvatar} alt={event.user} className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{event.user.charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <span className="text-xs font-bold text-[#172B4D] truncate max-w-[120px]">
                                    {event.user}
                                </span>
                                <span className="text-[10px] text-[#6B778C] whitespace-nowrap ml-auto">
                                    {formatDistanceToNow(event.timestamp, { addSuffix: true })}
                                </span>
                            </div>

                            <div className="text-xs text-[#42526E] leading-tight">
                                <span className="font-medium text-[#172B4D]">
                                    {event.type === 'status_update' && event.prevStatus
                                        ? `changed status from ${displayStatus(event.prevStatus)} to ${displayStatus(event.status)}`
                                        : event.actionLabel}
                                </span>
                                {event.description && event.type !== 'comment_added' && event.type !== 'reply_added' && (
                                    <span className="text-[#6B778C] ml-1">- {event.description.length > 50 ? event.description.substring(0, 50) + '...' : event.description}</span>
                                )}
                            </div>

                            {(event.type === 'comment_added' || event.type === 'reply_added') && (
                                <div className="mt-1.5 p-2 bg-[#F4F5F7] rounded border border-[#DFE1E6] text-xs text-[#172B4D] italic line-clamp-2">
                                    "{event.description}"
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {visibleCount < allEvents.length && (
                <div className="mt-4 flex justify-center">
                    <button
                        onClick={handleLoadMore}
                        className="text-[11px] font-bold text-[#0052CC] hover:text-[#0747A6] flex items-center gap-1 py-1 px-3 rounded hover:bg-[#DEEBFF] transition-all"
                    >
                        <ArrowPathIcon className="h-3 w-3" />
                        Load More
                    </button>
                </div>
            )}
        </div>
    );
};

export default UnifiedTimeline;
